/**
 * gd-overlay-portal.js — hoist full-viewport overlays to <body> root.
 *
 * Why:
 *   Sticky sections (announcement-bar, header) wrap their inner elements in
 *   `<section class="shopify-section">` with `position: sticky; z-index: …`.
 *   Per CSS spec, that creates a new stacking context. When the cart drawer,
 *   search panel, or bundle builder modals are nested inside another Shopify
 *   section wrapper that has any positioned ancestor with a stacking context,
 *   their `position: fixed; z-index: 9000` is rendered relative to that
 *   ancestor — not the root — so the sticky promo bar (also at the section-
 *   wrapper layer) can poke through.
 *
 *   Moving overlay DOM nodes to <body> as direct children sidesteps the
 *   problem entirely: their z-index is then resolved against the root.
 *
 * Portaled:
 *   [data-gd-cart-drawer]          cart drawer
 *   [data-gd-search]               search panel
 *   [data-gd-kit-builder-modal]    Salon Kit bundle builder
 *   [data-gd-instant-modal]        Instant Gels bundle builder
 *
 * NOT portaled:
 *   .gd-mega                       mega menu (anchored absolute to header,
 *                                  must stay inside .gd-header to position)
 *   .gd-mobile-drawer              mobile nav drawer (already body-level
 *                                  via position:fixed inside the header
 *                                  section, but that section's wrapper is
 *                                  the same as cart drawer; portal anyway
 *                                  to be safe — adds a 5th selector below)
 *
 * Idempotent. Safe to load on every page. Listener bindings on the moved
 * nodes survive the move (DOM listeners are not severed by appendChild).
 */
(() => {
  'use strict';

  const SELECTORS = [
    '[data-gd-cart-drawer]',
    '[data-gd-search]',
    '[data-gd-kit-builder-modal]',
    '[data-gd-instant-modal]',
    '.gd-mobile-drawer',
  ];

  function portal() {
    for (const sel of SELECTORS) {
      const el = document.querySelector(sel);
      if (!el) continue;
      // Skip if already a body child or marked
      if (el.parentElement === document.body) continue;
      if (el.dataset.gdPortaled === '1') continue;
      el.dataset.gdPortaled = '1';
      document.body.appendChild(el);
    }
  }

  // Run as early as possible — overlays are hidden by default so visual
  // re-flow on append is invisible. Subsequent late-load sections (theme
  // editor refresh) re-portal via the shopify:section:load listener.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', portal, { once: true });
  } else {
    portal();
  }
  document.addEventListener('shopify:section:load', portal);
})();
